import { Users } from '@platform/client';
import { GetCurrentUser200, OrgRepUser } from '@platform/client/schemas';
import BusinessChallengesSection from '../business-challenges';
import { RejoinPanel } from './RejoinPanel';
import {
  isCorporateProNoCohort,
  isInactiveUser,
  isNonCorporatePro,
} from '../../util/ProgramHelpers';
import { IndividualInactivePanel } from './IndividualInactiveUserPanel';
import CohortPanel from '../cohort-panel';

/** The main content panel of the dashboard home page */
const DashboardHomePanel = () => {
  const { isSuccess, data: user } = Users.useGetCurrentUser({
    query: {
      // Throw an error if we fail to fetch user data after retries are complete
      // TODO: Find a more graceful way to error the frontend when we are unable to fetch core app data like this, outside of the Dashboard feature
      throwOnError: true,
    },
  });
  return (
    <div className='px-4 sm:px-11 w-full min-w-0 flex flex-col gap-4'>
      <div className='flex flex-col gap-4'>
        <div className='text-3xl'>Welcome back{isSuccess && `, ${user?.firstName}`}</div>
        {isSuccess && user && <UserPanel user={user} />}
      </div>
    </div>
  );
};

/**
 * Picks the single panel the user sees. Pros without a corporate partner, and corporate pros
 * between cohorts, get a prompt to (re)join instead of the dashboard;
 */
const UserPanel = ({ user }: { user: GetCurrentUser200 }) => {
  // any non corporate pro, regardless of inactive status, gets the individual inactive panel
  if (isNonCorporatePro(user)) return <IndividualInactivePanel />;
  // inactive or cohort completed/dropped corp users get rejoin option
  if (isCorporateProNoCohort(user) || isInactiveUser(user)) return <RejoinPanel user={user} />;

  switch (user.accountType) {
    case 'organization':
      return <OrganizationDashboardHomePanel user={user} />;
    case 'experteer':
      return <ProfessionalDashboardHomePanel user={user} />;
    default:
      return null;
  }
};

const OrganizationDashboardHomePanel = ({ user }: { user: OrgRepUser }) => {
  if (!user?.organization?.id) {
    return null;
  }
  return <BusinessChallengesSection organizationId={user?.organization?.id} />;
};

const ProfessionalDashboardHomePanel = ({ user }: { user: GetCurrentUser200 }) => {
  return <div className='flex flex-col gap-4 mt-8'>{user?.cohort && <CohortPanel />}</div>;
};

export default DashboardHomePanel;
